import { useUIStore } from '@/store/uiStore';
import type { NotificationType } from '@/types';

interface ToastOptions {
  duration?: number;
  persistent?: boolean;
}

class ToastManager {
  private show(type: NotificationType, message: string, options?: ToastOptions) {
    const { showNotification } = useUIStore.getState();
    
    showNotification({
      type,
      message,
      duration: options?.persistent ? 0 : (options?.duration ?? 5000),
    });
  }
  
  success(message: string, options?: ToastOptions) {
    this.show('success', message, options);
  }
  
  error(message: string, options?: ToastOptions) {
    this.show('error', message, options);
  }
  
  warning(message: string, options?: ToastOptions) {
    this.show('warning', message, options);
  }
  
  info(message: string, options?: ToastOptions) {
    this.show('info', message, options);
  }
  
  promise<T>(
    promise: Promise<T>,
    {
      loading = 'Loading...',
      success = 'Success!',
      error = 'Something went wrong',
    }: {
      loading?: string;
      success?: string | ((data: T) => string);
      error?: string | ((error: unknown) => string);
    }
  ): Promise<T> {
    // Show loading toast
    const loadingId = crypto.randomUUID();
    const { showNotification, hideNotification } = useUIStore.getState();

    showNotification({
      type: 'info',
      message: loading,
      duration: 0, // Persistent
    });
    
    return promise
      .then((data) => {
        hideNotification(loadingId);
        const successMessage = typeof success === 'function' ? success(data) : success;
        this.success(successMessage);
        return data;
      })
      .catch((err) => {
        hideNotification(loadingId);
        const errorMessage = typeof error === 'function' ? error(err) : error;
        this.error(errorMessage);
        throw err;
      });
  }
  
  dismiss(id: string) {
    const { hideNotification } = useUIStore.getState();
    hideNotification(id);
  }
  
  dismissAll() {
    const { clearNotifications } = useUIStore.getState();
    clearNotifications();
  }
}

// Export singleton instance
export const toast = new ToastManager();

// Convenience hook for components
export function useToast() {
  const { showNotification, hideNotification, clearNotifications } = useUIStore();
  
  return {
    show: showNotification,
    hide: hideNotification,
    clear: clearNotifications,
    success: (message: string, options?: ToastOptions) => toast.success(message, options),
    error: (message: string, options?: ToastOptions) => toast.error(message, options),
    warning: (message: string, options?: ToastOptions) => toast.warning(message, options),
    info: (message: string, options?: ToastOptions) => toast.info(message, options),
    promise: toast.promise.bind(toast),
  };
}